import { useCallback, useContext } from 'react';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { SearchLoadingDispatchContext, SearchResultsDispatchContext } from '@/containers/WorkspaceGlobalSearch/Context';
import type { WorkspaceSearchFull } from '@trinity/chat';

export default function useSearchFetchAll() {
  const setResults = useContext(SearchResultsDispatchContext);
  const setLoading = useContext(SearchLoadingDispatchContext);

  const apiClient = useChatApiClient();

  const files = useCallback(async ({ filters }: FetchData) => {
    const result = await apiClient.workspacesGlobalSearch.files({
      filters: {
        keywords: filters.keywords,
        filetypes: filters.filetypes,
        tags: filters.tags,
      },
      size: 10,
    });

    if (result) {
      setResults(prev => ({
        ...prev,
        all: {
          ...prev.all,
          files: result,
        },
      }));
    }
  }, [setResults]);

  const folders = useCallback(async ({ filters }: FetchData) => {
    const result = await apiClient.workspacesGlobalSearch.folders({
      filters: {
        keywords: filters.keywords,
        tags: filters.tags,
      },
      size: 10,
    });

    if (result) {
      setResults(prev => ({
        ...prev,
        all: {
          ...prev.all,
          folders: result,
        },
      }));
    }
  }, [setResults]);

  const brands = useCallback(async ({ filters }: FetchData) => {
    const result = await apiClient.workspacesGlobalSearch.brands({
      filters: {
        keywords: filters.keywords,
        tags: filters.tags,
      },
      size: 10,
    });

    if (result) {
      setResults(prev => ({
        ...prev,
        all: {
          ...prev.all,
          brands: result,
        },
      }));
    }
  }, [setResults]);

  const projects = useCallback(async ({ filters }: FetchData) => {
    const result = await apiClient.workspacesGlobalSearch.projects({
      filters: {
        keywords: filters.keywords,
        tags: filters.tags,
      },
      size: 10,
    });

    if (result) {
      setResults(prev => ({
        ...prev,
        all: {
          ...prev.all,
          projects: result,
        },
      }));
    }
  }, [setResults]);

  const dashboards = useCallback(async ({ filters }: FetchData) => {
    const result = await apiClient.workspacesGlobalSearch.dashboards({
      filters: {
        keywords: filters.keywords,
        // tags: filters.tags,
      },
      size: 10,
    });

    if (result) {
      setResults(prev => ({
        ...prev,
        all: {
          ...prev.all,
          dashboards: result,
        },
      }));
    }
  }, [setResults]);

  const quantanalyses = useCallback(async ({ filters }: FetchData) => {
    const result = await apiClient.workspacesGlobalSearch.quantanalyses({
      filters: {
        keywords: filters.keywords,
      },
      size: 10,
    });

    if (result) {
      setResults(prev => ({
        ...prev,
        all: {
          ...prev.all,
          quantanalyses: result,
        },
      }));
    }
  }, [setResults]);

  const transcripts = useCallback(async ({ filters }: FetchData) => {
    const result = await apiClient.workspacesGlobalSearch.transcripts({
      filters: {
        keywords: filters.keywords,
        tags: filters.tags,
      },
      size: 10,
    });

    if (result) {
      setResults(prev => ({
        ...prev,
        all: {
          ...prev.all,
          transcripts: result,
        },
      }));
    }
  }, [setResults]);

  const fetch = useCallback(({ filters }: FetchData) => {
    setLoading(prev => ({ ...prev, all: { ...prev.all, loading: true } }));
    Promise.all([
      new Promise<void>(resolve => setTimeout(() => resolve(), 250)),
      files({ filters }),
      folders({ filters }),
      brands({ filters }),
      dashboards({ filters }),
      quantanalyses({ filters }),
      transcripts({ filters }),
      projects({ filters }),
    ])
      .then(() => {
        setLoading(prev => ({ ...prev, all: { loading: false } }));
      });
  }, [files, folders, brands, dashboards, quantanalyses, transcripts, projects, setLoading]);

  return fetch;
}

export { useSearchFetchAll };

type FetchData = {
  filters: Partial<WorkspaceSearchFull.Query>;
};